// Client-side FLAC-in-MP4 -> BW64 (Broadcast Wave) transcode.
//
// The recorder does not emit a raw .flac bitstream: ffmpeg muxes the FLAC codec
// into a *fragmented* MP4 (movflags empty_moov+default_base_moof, frag_duration),
// so the downloaded "FLAC" is actually fMP4 (ftyp/moov + moof/mdat). A raw-FLAC
// decoder (libflac.js) chokes on this with LOST_SYNC because it never sees the
// 'fLaC' marker. So we demux the fMP4 with mp4box.js and decode the FLAC track
// with the browser's own WebCodecs AudioDecoder -- both streaming, so a 6-8 h
// recording never has to fit in RAM. The decoded PCM is written as 32-bit float
// straight to disk via the File System Access API.
//
// Chromium-only: relies on window.showSaveFilePicker (File System Access API)
// and window.AudioDecoder (WebCodecs).

/* global AudioDecoder, EncodedAudioChunk */

import { createFile as mp4CreateFile, DataStream as MP4DataStream, Log as MP4Log } from 'mp4box';
import { Bw64Writer, bextTimingFromUtc } from './bw64Writer';

// mp4box's dfLa (FLAC config) parser over-reads by a few bytes and logs a benign
// "read N more bytes ... seeking backwards" error at its lowest (error) level --
// it recovers and we still extract a valid STREAMINFO. Silence just that one
// message so it does not look like a real failure; pass everything else through.
if (MP4Log && typeof MP4Log.error === 'function' && !MP4Log.__chronosPatched) {
    const originalError = MP4Log.error.bind(MP4Log);
    MP4Log.error = (module, msg) => {
        if (typeof msg === 'string' && msg.indexOf('dfLa') !== -1) return;
        originalError(module, msg);
    };
    MP4Log.__chronosPatched = true;
}

// Case-insensitive metadata lookup (blob storage may normalise key casing).
const pickMeta = (md, key) => {
    if (!md) return undefined;
    if (md[key] !== undefined) return md[key];
    const lower = key.toLowerCase();
    const found = Object.keys(md).find((k) => k.toLowerCase() === lower);
    return found ? md[found] : undefined;
};

const xmlEscapeCData = (s) => String(s).replace(/]]>/g, ']]]]><![CDATA[>');

// Wraps the chronos sidecar JSON in a minimal iXML document so the one BW64 file
// also carries the things bext cannot express (measured/drift sample rate, clock
// error bound). DAWs ignore unknown iXML sub-elements; tooling can read it back.
const buildIxml = (sidecarObj) => {
    const json = JSON.stringify(sidecarObj);
    return (
        '<?xml version="1.0" encoding="UTF-8"?>\n' +
        '<BWFXML>\n' +
        '  <IXML_VERSION>1.5</IXML_VERSION>\n' +
        '  <USER>\n' +
        '    <CHRONOS_SIDECAR><![CDATA[' + xmlEscapeCData(json) + ']]></CHRONOS_SIDECAR>\n' +
        '  </USER>\n' +
        '</BWFXML>\n'
    );
};

const baseName = (name) => (name.includes('.') ? name.slice(0, name.lastIndexOf('.')) : name);

export const isBw64ExportSupported = () =>
    typeof window !== 'undefined' &&
    typeof window.showSaveFilePicker === 'function' &&
    typeof window.AudioDecoder === 'function';

// Explains why the export is unavailable so the UI can point the user at the real
// cause. Both APIs are Chromium-only; the File System Access API additionally
// requires a secure context, so an otherwise-supported browser (Chrome/Edge/
// Brave) still lacks it on plain http:// over a LAN IP. Brave is a special case:
// it ships the File System Access API disabled by default for privacy reasons.
export const bw64UnsupportedReason = () => {
    if (typeof window === 'undefined') return 'Not running in a browser.';
    if (typeof window.AudioDecoder !== 'function') {
        return 'This browser lacks the WebCodecs API (window.AudioDecoder), which is needed to decode the audio. Use a recent Chromium browser (Chrome, Edge, or Brave).';
    }
    if (typeof window.showSaveFilePicker === 'function') return null;
    if (window.isSecureContext === false) {
        return 'This page is not served over a secure context. Open it via https:// or http://localhost (plain http:// on a LAN IP disables the File System Access API even in Chromium browsers).';
    }
    if (typeof navigator !== 'undefined' && navigator.brave) {
        return 'Brave disables the File System Access API by default. Enable it at brave://flags/#file-system-access-api (set to "Enabled") and relaunch Brave, or use Chrome/Edge.';
    }
    return 'This browser does not support the File System Access API. Use a Chromium browser (Chrome, Edge, or Brave with the File System Access API flag enabled).';
};

const toHex = (u8, n) => Array.from(u8.subarray(0, n)).map((b) => b.toString(16).padStart(2, '0')).join(' ');

// Builds the WebCodecs AudioDecoder `description` for a FLAC track from the MP4
// `dfLa` (FLACSpecificBox). WebCodecs wants the raw-FLAC header form: the 4-byte
// 'fLaC' signature followed by the STREAMINFO metadata block (with its block
// header). The dfLa box payload is: [8-byte box header][1 version][3 flags]
// [FLAC metadata block(s)]. So the description = 'fLaC' + everything after the
// version/flags.
const flacDescriptionFromTrak = (trak) => {
    const entries = trak && trak.mdia && trak.mdia.minf && trak.mdia.minf.stbl && trak.mdia.minf.stbl.stsd
        ? trak.mdia.minf.stbl.stsd.entries : null;
    if (!entries) return null;
    for (const entry of entries) {
        const dfla = entry.dfLa || (entry.boxes || []).find((b) => b.type === 'dfLa');
        if (!dfla) continue;
        const ds = new MP4DataStream(undefined, 0, MP4DataStream.BIG_ENDIAN);
        dfla.write(ds);
        const boxBytes = new Uint8Array(ds.buffer); // full dfLa box incl. 8-byte header
        const metaBlocks = boxBytes.subarray(8 + 4); // skip box header + version/flags
        const desc = new Uint8Array(4 + metaBlocks.length);
        desc.set([0x66, 0x4c, 0x61, 0x43], 0); // 'fLaC'
        desc.set(metaBlocks, 4);
        return desc;
    }
    return null;
};

// Copies a WebCodecs AudioData (any internal format) into an interleaved 32-bit
// float little-endian PCM byte payload. Returns { interleaved, bytes, frames }.
// `interleaved` (the Float32Array) is also returned so the UTC-align path can feed
// it straight into the resampler without re-parsing the bytes.
const audioDataToFloatBytes = (audioData) => {
    const frames = audioData.numberOfFrames;
    const channels = audioData.numberOfChannels;
    const interleaved = new Float32Array(frames * channels);
    const plane = new Float32Array(frames);
    for (let c = 0; c < channels; c++) {
        audioData.copyTo(plane, { planeIndex: c, format: 'f32-planar' });
        if (channels === 1) {
            interleaved.set(plane);
        } else {
            for (let i = 0; i < frames; i++) interleaved[i * channels + c] = plane[i];
        }
    }
    return { interleaved, bytes: new Uint8Array(interleaved.buffer), frames };
};

// Prompt for the BW64 destination. Call this synchronously from the click
// handler (before any await) so the browser's user-activation window is still
// valid: the File System Access API rejects showSaveFilePicker otherwise
// ("Must be handling user gesture to show a file picker"). Pass the returned
// handle to downloadFlacAsBw64.
export const pickBw64SaveFile = (fileName) =>
    window.showSaveFilePicker({
        suggestedName: `${baseName(fileName)}.wav`,
        types: [{ description: 'Broadcast Wave (BW64)', accept: { 'audio/wav': ['.wav'] } }]
    });

// Transcodes a FLAC-in-MP4 download into a BW64 file chosen by the user.
//   flacResponse - a fetch() Response whose .body is the fMP4 byte stream
//   fileName     - the source file name (used for the suggested .wav name)
//   metadata     - the blob metadata (sidecar) used for bext timing + iXML
//   sidecar      - the structured sidecar object to embed as iXML (optional)
//   fileHandle   - a save handle already obtained via pickBw64SaveFile (optional;
//                  when omitted the picker is shown here, which requires this call
//                  to still be within a user gesture)
//   onProgress   - (fraction 0..1) progress callback (optional)
//   resampleToUtc- when true, stream the decoded PCM through libsamplerate to
//                  correct the recorder's constant clock drift, so the output maps
//                  1:1 to real UTC time at the nominal rate (see setup()).
// Returns { cancelled: true } if the user dismisses the save dialog.
export const downloadFlacAsBw64 = async ({ flacResponse, fileName, metadata, sidecar, fileHandle, onProgress, resampleToUtc = false }) => {
    if (!isBw64ExportSupported()) {
        throw new Error('BW64 export needs a Chromium browser (File System Access + WebCodecs).');
    }
    if (!flacResponse || !flacResponse.body) {
        throw new Error('No audio stream to read.');
    }

    // Ask for the destination up front so the write stream is ready before we
    // start decoding (and so a user cancel aborts cheaply). If the caller already
    // picked a destination inside the click gesture, reuse that handle.
    let handle = fileHandle || null;
    if (!handle) {
        try {
            handle = await window.showSaveFilePicker({
                suggestedName: `${baseName(fileName)}.wav`,
                types: [{ description: 'Broadcast Wave (BW64)', accept: { 'audio/wav': ['.wav'] } }]
            });
        } catch (err) {
            if (err && err.name === 'AbortError') return { cancelled: true };
            throw err;
        }
    }
    const writable = await handle.createWritable();

    const mp4 = mp4CreateFile();
    const reader = flacResponse.body.getReader();

    // Progress is driven by bytes read off the network stream: the recorder muxes
    // into a *fragmented* MP4 with an empty_moov, so info.duration is 0 and a
    // frame-count total is unknown up front. The compressed input size (from
    // Content-Length) is known, and decode advances roughly linearly with it, so
    // it is the meaningful progress signal (and mirrors the growing temp file).
    const totalBytes = Number(flacResponse.headers.get('Content-Length')) || 0;
    let readBytes = 0;

    let writer = null;
    let decoder = null;
    let decodeError = null;
    let expectedFrames = 0;   // total audio frames (for progress), 0 if unknown
    let decodedFrames = 0;

    // --- disk write serialisation ---
    // The WebCodecs output callback is synchronous, but writing to disk is async.
    // We chain writes onto a single promise so PCM lands in order, and track the
    // in-flight byte volume so we can throttle the demux/feed loop (bounded RAM).
    let writeChain = Promise.resolve();
    let pendingWriteBytes = 0;
    const enqueueWrite = (bytes) => {
        pendingWriteBytes += bytes.length;
        writeChain = writeChain.then(async () => {
            await writer.writePcm(bytes);
            pendingWriteBytes -= bytes.length;
        }).catch((e) => { decodeError = decodeError || e; });
    };

    // --- UTC-alignment resampler (optional) ---
    // When resampleToUtc is set, decoded PCM is streamed through a libsamplerate
    // instance (loaded lazily) before hitting disk. libsamplerate's `full` API is
    // stateful, so chunks MUST be fed in order -- the single writeChain guarantees
    // that. The instance is created in setup() once channels + rates are known;
    // srcReady resolves to it, srcInstance holds it for destroy() in finally.
    let srcReady = null;    // Promise<SRC> | null
    let srcInstance = null; // resolved SRC handle (cleanup)
    const enqueueResampleWrite = (interleaved) => {
        const approxBytes = interleaved.byteLength;
        pendingWriteBytes += approxBytes; // input-size backpressure (output ~= input)
        writeChain = writeChain.then(async () => {
            const src = await srcReady;
            const out = src.full(interleaved); // interleaved Float32, resampled
            if (out && out.length) {
                // out may be a view over the resampler's reused buffer; copy so the
                // async disk write cannot race the next full() call.
                await writer.writePcm(new Uint8Array(out.slice().buffer));
            }
            pendingWriteBytes -= approxBytes;
        }).catch((e) => { decodeError = decodeError || e; pendingWriteBytes -= approxBytes; });
    };

    // --- one-time setup once the fMP4 init segment (moov) has been parsed ---
    // Runs *synchronously* from onReady: for a short recording the whole fMP4
    // (incl. moof/mdat fragments) can be appended before the disk header write
    // would resolve, and mp4box only retains sample payloads once extraction has
    // been armed. So we arm extraction (configure + setExtractionOptions + start)
    // here without awaiting, and gate the PCM writes behind the header write via
    // the write chain instead.
    let ready = false;
    let setupError = null;
    let samplesFed = 0;
    const setup = (info) => {
        const track = (info.audioTracks && info.audioTracks[0]) || null;
        if (!track) throw new Error('No audio track found in the MP4 container.');

        const sampleRate = track.audio.sample_rate;
        const channels = track.audio.channel_count;
        const durationSec = info.duration && info.timescale ? info.duration / info.timescale : 0;
        expectedFrames = durationSec > 0 ? Math.round(durationSec * sampleRate) : 0;

        // UTC-alignment resampler setup: correct the constant clock-drift ratio
        // between the recorder's true (measured) sample rate and the file's nominal
        // rate, so the exported audio maps 1:1 to real UTC time when played at the
        // nominal rate. measuredRate comes from the recorder (sampleCount / UTC-span,
        // stored in metadata as "sampleRate"); nominalRate is what the FLAC/MP4 header
        // declares. ratio = nominal/measured (~1 +/- a few ppm for a TCXO). The output
        // header still declares the nominal rate -- only the sample COUNT changes.
        if (resampleToUtc) {
            const measuredRate = Number(pickMeta(metadata, 'sampleRate'));
            if (!isFinite(measuredRate) || measuredRate <= 0) {
                throw new Error('UTC-align needs the recorder\'s measured sample rate (metadata "sampleRate"), which is missing for this file.');
            }
            const ratio = sampleRate / measuredRate;
            // A real TCXO drifts only a few ppm, so a ratio off by more than 0.2% is
            // corrupt metadata, not drift -- refuse rather than wreck the audio.
            if (Math.abs(ratio - 1) > 0.002) {
                throw new Error(`UTC-align refused: measured rate ${measuredRate} Hz vs nominal ${sampleRate} Hz implies ${((ratio - 1) * 1e6).toFixed(0)} ppm drift, which is implausible (corrupt metadata?).`);
            }
            // libsamplerate-js marshals the rates to WASM as C `int`s (src_ratio =
            // outSr / (double)inSr) AND validates each is within 1..192000. So we can
            // neither pass 48000.7 directly (truncates to 48000 -> ratio 1.0, no
            // correction) nor scale both up (exceeds 192000). Instead approximate the
            // desired ratio (nominal/measured) by the best integer pair outSr/inSr with
            // both <= 192000, via continued-fraction convergents. For a few-ppm drift
            // this lands ~0.02 ppm off -- below the metadata precision and clock noise.
            const MAX_RATE = 192000;
            let h0 = 0, h1 = 1, k0 = 1, k1 = 0;   // convergent numerators / denominators
            let outSr = 1, inSr = 1;              // fallback = ratio 1 (no resample)
            let x = ratio;
            for (let i = 0; i < 64; i++) {
                const a = Math.floor(x);
                const h2 = a * h1 + h0;   // -> outSr (numerator)
                const k2 = a * k1 + k0;   // -> inSr  (denominator)
                if (h2 > MAX_RATE || k2 > MAX_RATE) break;
                if (h2 >= 1 && k2 >= 1) { outSr = h2; inSr = k2; }
                h0 = h1; h1 = h2; k0 = k1; k1 = k2;
                const frac = x - a;
                if (frac < 1e-15) break;
                x = 1 / frac;
            }
            const actualPpm = ((outSr / inSr) - 1) * 1e6;
            console.info('[bw64] UTC-align resample', {
                measuredRate, nominalRate: sampleRate, inSr, outSr,
                targetPpm: Number(((ratio - 1) * 1e6).toFixed(2)),
                actualPpm: Number(actualPpm.toFixed(3))
            });
            srcReady = (async () => {
                const { create, ConverterType } = await import('@alexanderolsen/libsamplerate-js');
                const src = await create(channels, inSr, outSr, {
                    converterType: ConverterType.SRC_SINC_BEST_QUALITY
                });
                srcInstance = src;
                return src;
            })();
            // Surface a resampler init failure into the main decode error path.
            srcReady.catch((e) => { decodeError = decodeError || e; });
        }

        const trak = mp4.getTrackById(track.id);
        const description = flacDescriptionFromTrak(trak);
        console.info('[bw64] track', { codec: track.codec, sampleRate, channels, durationSec });
        if (description) {
            console.info('[bw64] flac description', description.length, 'bytes:', toHex(description, 16));
        } else {
            console.warn('[bw64] no dfLa description found; AudioDecoder.configure may fail');
        }

        decoder = new AudioDecoder({
            output: (audioData) => {
                try {
                    const { interleaved, bytes, frames } = audioDataToFloatBytes(audioData);
                    decodedFrames += frames;
                    if (resampleToUtc) enqueueResampleWrite(interleaved);
                    else enqueueWrite(bytes);
                    // Byte-based progress (below) is the primary signal; fall back to
                    // frame-based only when the input size is unknown.
                    if (onProgress && totalBytes === 0 && expectedFrames > 0) {
                        onProgress(Math.min(1, decodedFrames / expectedFrames));
                    }
                } catch (e) {
                    decodeError = decodeError || e;
                } finally {
                    audioData.close();
                }
            },
            error: (e) => { decodeError = decodeError || e; console.error('[bw64] AudioDecoder error:', e); }
        });
        decoder.configure({
            codec: 'flac',
            sampleRate,
            numberOfChannels: channels,
            ...(description ? { description } : {})
        });

        // --- BW64 header (32-bit float PCM) from the sidecar + track info ---
        const startUtc = pickMeta(metadata, 'startUtc');
        const timing = bextTimingFromUtc(startUtc, sampleRate);
        writer = new Bw64Writer({
            writable,
            sampleRate,
            channels,
            bitsPerSample: 32,
            float: true,
            bext: {
                description: `chronos recording ${fileName}`,
                originator: 'chronos',
                originatorReference: pickMeta(metadata, 'sessionId') || '',
                originationDate: timing.originationDate,
                originationTime: timing.originationTime,
                timeReferenceSamples: timing.timeReferenceSamples
            },
            ixml: sidecar ? buildIxml(sidecar) : null
        });
        // Kick off the header write; all PCM writes chain behind it (in order).
        writeChain = writer.writeHeader().catch((e) => { decodeError = decodeError || e; });

        mp4.onSamples = (id, _user, samples) => {
            if (decodeError) return;
            for (const s of samples) {
                const chunk = new EncodedAudioChunk({
                    type: s.is_sync ? 'key' : 'delta',
                    timestamp: Math.round((s.cts * 1e6) / s.timescale),
                    duration: Math.round((s.duration * 1e6) / s.timescale),
                    data: s.data
                });
                decoder.decode(chunk);
            }
            samplesFed += samples.length;
            mp4.releaseUsedSamples(id, samples[samples.length - 1].number);
        };
        mp4.setExtractionOptions(track.id, null, { nbSamples: 1000 });
        mp4.start();
        ready = true;
    };

    mp4.onReady = (info) => { try { setup(info); } catch (e) { setupError = e; } };
    mp4.onError = (e) => { setupError = setupError || new Error(`MP4 parse error: ${e}`); };

    // Yields to the event loop so pending AudioDecoder outputs / disk writes can
    // drain, keeping memory bounded while the demuxer runs ahead of the decoder.
    const tick = () => new Promise((r) => setTimeout(r, 0));
    const drainBelow = async () => {
        while (!decodeError && decoder &&
            (decoder.decodeQueueSize > 128 || pendingWriteBytes > (8 << 20))) {
            // eslint-disable-next-line no-await-in-loop
            await tick();
        }
    };

    let fileOffset = 0;
    try {
        // --- feed the network stream into mp4box until the init segment lands ---
        // eslint-disable-next-line no-constant-condition
        while (true) {
            // eslint-disable-next-line no-await-in-loop
            const { value, done } = await reader.read();
            if (setupError) throw setupError;
            if (done) break;
            const ab = value.buffer.slice(value.byteOffset, value.byteOffset + value.byteLength);
            ab.fileStart = fileOffset;
            fileOffset += value.byteLength;
            readBytes += value.byteLength;
            // Cap at 0.99 while reading; the decoder/writer drain afterwards and the
            // final onProgress(1) fires once the file is fully written to disk.
            if (onProgress && totalBytes > 0) {
                onProgress(Math.min(0.99, readBytes / totalBytes));
            }
            mp4.appendBuffer(ab);
            if (decodeError) throw decodeError;
            if (ready) {
                // eslint-disable-next-line no-await-in-loop
                await drainBelow();
            }
        }
        if (setupError) throw setupError;
        if (!ready) throw new Error('Could not parse the MP4 init segment (no moov/audio track).');

        mp4.flush();               // emit any remaining buffered samples
        await decoder.flush();     // drain all queued decodes -> output callbacks
        await writeChain;          // wait for all PCM to hit disk
        if (decodeError) throw decodeError;

        console.info('[bw64] done', { samplesFed, decodedFrames, dataBytes: writer.dataBytes });
        if (decodedFrames === 0) {
            throw new Error(`Decoded 0 audio frames (fed ${samplesFed} coded samples). The audio track produced no PCM.`);
        }

        await writer.finalize();
        await writable.close();
        if (onProgress) onProgress(1);
        return { cancelled: false };
    } catch (err) {
        try { await writable.abort?.(); } catch (_) { /* ignore */ }
        throw err;
    } finally {
        try { if (decoder && decoder.state !== 'closed') decoder.close(); } catch (_) { /* ignore */ }
        try { if (srcInstance) srcInstance.destroy(); } catch (_) { /* ignore */ }
        try { reader.releaseLock?.(); } catch (_) { /* ignore */ }
    }
};
